import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Decimal } from 'decimal.js';
import { ZERO_TABLE, zeroOdds, zeroText, zeroPair, boundaryText } from '../lib/zero';
import type { Row } from '../lib/row';
import type { Meta } from './meta';
import { venueName } from './meta';
import './ZeroGuide.css';

const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
export const zeroHref = `${base}/zero-arbs`;
const PIN = 'forq.zero-guide.pinned';
function savedPin(): boolean { try { return localStorage.getItem(PIN) === '1'; } catch { return false; } }
function savePin(value: boolean): void { try { localStorage.setItem(PIN, value ? '1' : '0'); } catch { /* private tab */ } }

export function ZeroTable({ initial = '1,80', full = false }: { initial?: string; full?: boolean }): React.ReactElement {
  const [query, setQuery] = useState(initial.replace('.', ','));
  const answer = zeroOdds(query);
  const input = useRef<HTMLInputElement>(null);
  const active = useRef<HTMLTableRowElement>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const key = useId();
  useEffect(() => {
    const box = scroll.current, row = active.current;
    if (full || !box || !row) return;
    // Scroll only the list: scrollIntoView also hides the header/close button
    // in the small calculator popup by scrolling the surrounding panel.
    const target = row.getBoundingClientRect(), viewport = box.getBoundingClientRect();
    box.scrollTop += target.top - viewport.top - (box.clientHeight - target.height) / 2;
  }, [query, full]);
  const match = (() => { try { return new Decimal(query.replace(',', '.')).toFixed(2); } catch { return ''; } })();
  const rows = ZERO_TABLE;
  return <div className={`nx-zero-table${full ? ' full' : ''}`}>
    <label htmlFor={key}>мой коэффициент</label>
    <input ref={input} id={key} inputMode="decimal" autoComplete="off" value={query}
      onChange={e => setQuery(e.target.value)} aria-describedby={`${key}-answer`} />
    <div className="nx-zero-answer" id={`${key}-answer`}>
      {answer ? <><span>второй для нуля ≈ <b>{answer.toDecimalPlaces(6).toString().replace('.', ',')}</b></span>
        <span>при шаге 0,01 — не ниже <b>{zeroText(answer)}</b></span></>
        : <span>введите коэффициент больше 1</span>}
    </div>
    <p>два противоположных исхода, без комиссий. выше порога — плюс, ниже — минус при пересчёте сумм.</p>
    {full && <p>в каждой паре: слева — первый кэф, справа — минимальный второй при шаге 0,01.</p>}
    <div ref={scroll} className="nx-zero-scroll" tabIndex={0} aria-label="таблица коэффициентов">
      <table><thead><tr><th>первый кэф</th><th>второй не ниже*</th></tr></thead>
        <tbody>{rows.map(r => <tr key={r.first} ref={r.first === match ? active : undefined} className={r.first === match ? 'chosen' : undefined}>
          <td><button type="button" onClick={() => setQuery(r.first.replace('.', ','))}>{r.first.replace('.', ',')}</button></td>
          <td>{zeroText(r.second)}</td>
        </tr>)}</tbody>
      </table>
    </div>
    <p>* округлено вверх к шагу 0,01. суммы и комиссии проверяйте в калькуляторе. для кэфа выше 2 используйте поле ввода.</p>
  </div>;
}

export function ZeroTools({ initial, dock = false }: { initial?: string; dock?: boolean }): React.ReactElement {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(() => dock && savedPin());
  const [pinned, setPinned] = useState(savedPin);
  const [doc, setDoc] = useState<Document | null>(null);
  useEffect(() => { setDoc(button.current?.ownerDocument ?? document); }, []);
  const close = (): void => { setOpen(false); setPinned(false); savePin(false); button.current?.focus(); };
  useEffect(() => {
    if (!doc) return;
    const other = (e: Event): void => { if ((e as CustomEvent<string>).detail !== id) setOpen(false); };
    const key = (e: KeyboardEvent): void => { if (open && e.key === 'Escape') { e.stopPropagation(); close(); } };
    const outside = (e: PointerEvent): void => {
      if (open && !pinned && !panel.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) setOpen(false);
    };
    doc.addEventListener('forq-zero-open', other); doc.addEventListener('keydown', key); doc.addEventListener('pointerdown', outside);
    return () => { doc.removeEventListener('forq-zero-open', other); doc.removeEventListener('keydown', key); doc.removeEventListener('pointerdown', outside); };
  }, [doc, id, open, pinned]);
  return <>
    <button ref={button} type="button" className="nx-zero-trigger" aria-expanded={open} aria-controls={id}
      onClick={e => {
        e.stopPropagation();
        if (open) { close(); return; }
        doc?.dispatchEvent(new CustomEvent('forq-zero-open', { detail: id })); setOpen(true);
      }}>нулевые вилки</button>
    {open && doc && createPortal(<div className="nx-root nx-zero-portal" onClick={e => e.stopPropagation()}>
      <aside ref={panel} id={id} className="nx-zero-panel" aria-label="нулевые вилки">
        <div className="nx-zero-title"><b>нулевые вилки</b>
          <button type="button" aria-pressed={pinned} onClick={() => { setPinned(!pinned); savePin(!pinned); }}>{pinned ? 'открепить' : 'закрепить'}</button>
          <button type="button" aria-label="закрыть таблицу" onClick={close}>×</button>
        </div>
        <ZeroTable initial={initial} />
        <div className="nx-zero-links"><a href={zeroHref} target="_blank" rel="noreferrer">отдельно и для печати ↗</a>
          <a href={`${base}/zero-arbs.pdf`} download>PDF ↓</a><a href={`${base}/zero-arbs.png`} download>картинка ↓</a></div>
      </aside>
    </div>, doc.body)}
  </>;
}

export function ZeroHint({
  row,
  meta,
  stale = false,
  quiet = false,
}: {
  row: Row;
  meta: Meta;
  stale?: boolean;
  /** Только кнопка «нулевые вилки», без порогов и «как читать». */
  quiet?: boolean;
}): React.ReactElement {
  const pair = zeroPair(row);
  const [details, setDetails] = useState(false);
  return <div className={`nx-zero-hint${quiet ? ' quiet' : ''}`} data-zero-hint>
    <div className="nx-zero-summary"><ZeroTools initial={row.legs[0]?.odds} />
      {!quiet && (pair.reason ? <span>{pair.reason}</span> : <>
        {pair.boundaries.map(b => <span key={b.source} title={`сейчас ${b.current.toString().replace('.', ',')}${b.unit === 'cents' ? ' ¢' : ''}`}>
          {venueName(meta, b.source)}: <b>{boundaryText(b)}</b></span>)}
        <button type="button" aria-expanded={details} onClick={() => setDetails(!details)}>как читать</button>
      </>)}
    </div>
    {!quiet && !pair.reason && pair.note.startsWith('комиссия долей') && <p>{pair.note}</p>}
    {!quiet && details && !pair.reason && <p>{pair.note}. каждый порог — при неизменном другом плече.
      фактический результат после округления — в калькуляторе. доступность ставки и правила расчёта проверяйте в конторах.</p>}
    {!quiet && stale && <span className="nx-zero-old">порог по сохранённым данным</span>}
  </div>;
}

export function ZeroPage(): React.ReactElement {
  useEffect(() => { document.title = 'Нулевые вилки — FORQ'; }, []);
  return <main className="nx-root nx-zero-page">
    <article><div className="nx-zero-title"><h1>нулевые вилки</h1><span>FORQ · 21.09.2026</span></div>
      <p>какой коэффициент нужен на втором плече, чтобы выйти в ноль.</p>
      <nav className="nx-zero-links"><a href={`${base}/app`}>к ленте</a>
        <button type="button" onClick={() => window.print()}>распечатать</button>
        <a href={`${base}/zero-arbs.pdf`} download>скачать PDF</a><a href={`${base}/zero-arbs.png`} download>скачать картинку</a></nav>
      <ZeroTable full />
      <section className="nx-zero-faq"><h2>как пользоваться</h2>
        <p>найдите коэффициент первого плеча. второй должен быть не ниже значения справа. при 1,80 / 2,25 результат — ноль, при 1,80 / 2,30 — около +0,98%, при 1,80 / 2,20 — −1% от общей суммы.</p>
        <p>после изменения коэффициента пересчитайте вторую сумму. порог предполагает правильное распределение ставок, два противоположных исхода и одинаковые правила расчёта.</p>
        <p>1,90 / 2,11 дают небольшой минус: точный порог — 2,111111… поэтому в таблице указано 2,12. это минимальный кэф при шаге 0,01, а не точный ноль.</p>
        <p>комиссии, ограничения суммы и доступный объём могут изменить результат. для прогнозных рынков меньше цена доли — лучше; обычная таблица комиссию долей не учитывает.</p>
        <p>три исхода, ставки «против», фрибеты и частичные возвраты требуют отдельного расчёта.</p>
        <p>стрим 20.09: 03:13 — объяснение; 38:25–39:16 — где держать таблицу. <a href="https://t.me/c/4386283004/27/1572" target="_blank" rel="noreferrer">запись в группе ↗</a></p>
      </section>
    </article>
  </main>;
}
